import React, { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Space,
  Spin,
  Typography,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
// Hooks
import { useGetPriceMargins } from 'hooks/useGetPriceMargins';
// Api
import { GET_STORE_NAME } from 'api/store/queries';
import { SET_STORE_PRICE_MARGINS } from 'api/margin/mutations';
// Types
import {
  GetStoreName,
  GetStoreNameVariables,
} from 'api/store/types/GetStoreName';
import {
  SetStorePriceMargins,
  SetStorePriceMarginsVariables,
} from 'api/margin/types/SetStorePriceMargins';
// UI
import { errorNotification, successNotification } from 'ui/Notification';

const { confirm } = Modal;

type StoreMarginsPageProps = {
  heading: string;
  description?: string;
};

type PriceMarginsFormValues = {
  merchMargin: number;
  streamMargin: number;
  amaMargin: number;
  tipMargin: number;
  productMargin: number;
};

const { Title, Paragraph, Text } = Typography;

const StoreMarginsPage: React.FC<StoreMarginsPageProps> = ({
  heading,
  description,
}) => {
  const { storeId } = useParams<{ storeId: string }>();
  const [form] = Form.useForm<PriceMarginsFormValues>();

  const { data: storeNameData } = useQuery<GetStoreName, GetStoreNameVariables>(
    GET_STORE_NAME,
    {
      skip: !storeId,
      variables: storeId
        ? {
            input: {
              id: storeId,
            },
          }
        : undefined,
      fetchPolicy: 'cache-first',
    }
  );

  const {
    data: marginsData,
    loading: marginsLoading,
    refetch,
    getStorePriceMargins: storePriceMargins,
  } = useGetPriceMargins({
    storeId,
    skip: !storeId,
  });

  const { data: defaultMarginsData } = useGetPriceMargins();
  const defaultMargins = defaultMarginsData?.getPriceMargins;
  const storeMargins = marginsData?.getPriceMargins;
  const didInitializeForm = useRef(false);

  const [setStorePriceMargins, { loading: saving }] = useMutation<
    SetStorePriceMargins,
    SetStorePriceMarginsVariables
  >(SET_STORE_PRICE_MARGINS);

  useEffect(() => {
    didInitializeForm.current = false;
    form.resetFields();
  }, [form, storeId]);

  useEffect(() => {
    if (didInitializeForm.current) return;

    if (storeMargins) {
      const formValues: PriceMarginsFormValues = {
        merchMargin: storeMargins.merchMargin,
        streamMargin: storeMargins.streamMargin,
        amaMargin: storeMargins.amaMargin,
        tipMargin: storeMargins.tipMargin,
        productMargin:
          storePriceMargins?.productMargin ??
          defaultMargins?.productMargin ??
          0,
      };

      form.setFieldsValue(formValues);
      didInitializeForm.current = true;
      return;
    }

    if (!marginsLoading && defaultMargins) {
      form.setFieldsValue({
        merchMargin: defaultMargins.merchMargin,
        streamMargin: defaultMargins.streamMargin,
        amaMargin: defaultMargins.amaMargin,
        tipMargin: defaultMargins.tipMargin,
      });
      didInitializeForm.current = true;
    }
  }, [form, storeMargins, defaultMargins, marginsLoading, storePriceMargins]);

  const handleSubmit = async () => {
    try {
      if (!storeId) {
        throw new Error('Missing store identifier');
      }

      const values = await form.validateFields();

      const input: SetStorePriceMarginsVariables['input'] = {
        storeId,
        amaMargin: values.amaMargin,
        merchMargin: values.merchMargin,
        streamMargin: values.streamMargin,
        tipMargin: values.tipMargin,
        productMargin: values.productMargin,
      };

      await setStorePriceMargins({
        variables: {
          input,
        },
      });

      successNotification('Store margins updated successfully');
      await refetch();
    } catch (error) {
      errorNotification((error as Error).message);
    }
  };

  const handleReset = () => {
    if (!defaultMargins) {
      return;
    }

    confirm({
      title: 'Reset to global defaults?',
      icon: <ExclamationCircleOutlined />,
      content:
        'This will reset all margin values to their global defaults. Any store-specific customizations will be lost.',
      okText: 'Confirm',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          if (!storeId) {
            throw new Error('Missing store identifier');
          }

          // Set form values to defaults
          form.setFieldsValue({
            merchMargin: defaultMargins.merchMargin,
            streamMargin: defaultMargins.streamMargin,
            amaMargin: defaultMargins.amaMargin,
            tipMargin: defaultMargins.tipMargin,
            productMargin: defaultMargins.productMargin,
          });

          // Submit the form with default values to backend
          const input: SetStorePriceMarginsVariables['input'] = {
            storeId,
            amaMargin: defaultMargins.amaMargin,
            merchMargin: defaultMargins.merchMargin,
            streamMargin: defaultMargins.streamMargin,
            tipMargin: defaultMargins.tipMargin,
            productMargin: defaultMargins.productMargin,
          };

          await setStorePriceMargins({
            variables: {
              input,
            },
          });

          successNotification('Store margins reset to global defaults');
          await refetch();
        } catch (error) {
          errorNotification((error as Error).message);
        }
      },
    });
  };

  const storeDisplayName = useMemo(() => {
    const storeName = storeNameData?.store?.storeDetails?.storeName;
    if (storeName) return storeName;

    return storeId || '';
  }, [storeId, storeNameData]);

  const formatMargin = (value?: number) =>
    typeof value === 'number' ? `${value}%` : '—';

  return (
    <div className="store-margins-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3}>{heading}</Title>
          {storeDisplayName && (
            <Paragraph type="secondary">Store: {storeDisplayName}</Paragraph>
          )}
        </div>

        <Card>
          {description && (
            <Paragraph style={{ marginBottom: 24 }}>{description}</Paragraph>
          )}

          <Spin spinning={marginsLoading}>
            <Form form={form} layout="vertical">
              <Form.Item
                label="Merch margin (%)"
                name="merchMargin"
                extra={
                  defaultMargins ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Default: {formatMargin(defaultMargins.merchMargin)}
                    </Text>
                  ) : undefined
                }
                rules={[
                  { required: true, message: 'Merch margin is required' },
                ]}
              >
                <InputNumber min={0} max={100} step={0.5} />
              </Form.Item>
              <Form.Item
                label="Stream margin (%)"
                name="streamMargin"
                extra={
                  defaultMargins ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Default: {formatMargin(defaultMargins.streamMargin)}
                    </Text>
                  ) : undefined
                }
                rules={[
                  { required: true, message: 'Stream margin is required' },
                ]}
              >
                <InputNumber min={0} max={100} step={0.5} />
              </Form.Item>
              <Form.Item
                label="AMA margin (%)"
                name="amaMargin"
                extra={
                  defaultMargins ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Default: {formatMargin(defaultMargins.amaMargin)}
                    </Text>
                  ) : undefined
                }
                rules={[{ required: true, message: 'AMA margin is required' }]}
              >
                <InputNumber min={0} max={100} step={0.5} />
              </Form.Item>
              <Form.Item
                label="Tip margin (%)"
                name="tipMargin"
                extra={
                  defaultMargins ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Default: {formatMargin(defaultMargins.tipMargin)}
                    </Text>
                  ) : undefined
                }
                rules={[{ required: true, message: 'Tip margin is required' }]}
              >
                <InputNumber min={0} max={100} step={0.5} />
              </Form.Item>
              <Form.Item
                label="Experience/Product/Memorabilia Margin (%)"
                name="productMargin"
                extra={
                  defaultMargins ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Default: {formatMargin(defaultMargins.productMargin)}
                    </Text>
                  ) : undefined
                }
              >
                <InputNumber min={0} max={100} step={0.5} />
              </Form.Item>
            </Form>
          </Spin>

          <Space style={{ marginTop: 24 }}>
            <Button onClick={handleReset}>Reset</Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={saving}
              disabled={marginsLoading || !storeId}
            >
              Save changes
            </Button>
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default StoreMarginsPage;
